/**
 * Prompt Version types for Agent versioning.
 *
 * The `PromptJson` document shape lives in `schemas/Agent/promptJson.schema.ts`
 * (Zod-inferred) — import it from `@nexus/types` directly.
 */

export enum PromptVersionType {
  AUTO = "AUTO",
  CHECKPOINT = "CHECKPOINT"
}

export interface VersionCreator {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

/**
 * Version metadata (no promptJson) — used by list responses and the version drawer.
 */
export interface PromptVersionMetadata {
  id: string;
  type: PromptVersionType;
  name: string | null;
  description: string | null;
  isProduction: boolean;
  createdAt: string;
  creator: VersionCreator | null;
  /** Number of AUTO versions between this checkpoint and the previous one (only set when type filter is CHECKPOINT) */
  autoSaveCountBefore?: number;
  /** createdAt of the preceding checkpoint as an ISO string; null for the oldest checkpoint (only set when type filter is CHECKPOINT) */
  prevCheckpointAt?: string | null;
}

/**
 * Used by the public v1 API surface; the new `@nexus/api/agent-versions` schemas
 * (server-side validated, frontend-generated) carry the typed promptJson shape.
 */
export interface RestoreVersionResponseDto {
  promptJson: Record<string, unknown>;
  message: string;
}

export interface DeleteVersionResponseDto {
  id: string;
  deleted: boolean;
}

/**
 * Grouped auto-saves between checkpoints for UI display.
 */
export interface AutoSaveGroup {
  type: "AUTO_SAVE_GROUP";
  startDate: string;
  endDate: string;
  autoSaves: PromptVersionMetadata[];
}

/**
 * Union type for version list items (can be a version or a group of auto-saves).
 */
export type VersionListItem = PromptVersionMetadata | AutoSaveGroup;

export function isAutoSaveGroup(item: VersionListItem): item is AutoSaveGroup {
  return "type" in item && item.type === "AUTO_SAVE_GROUP";
}
